/**
 * Parts Manager Component
 * Component منفصل فقط لإدارة Parts (إضافة، حذف، تعديل)
 */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Plus, Trash2, Edit3, Play } from 'lucide-react';
import { formatTime } from '../../utils/partsUtils';

const PartsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PartItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border: 1px solid ${props => props.theme.border || '#ccc'};
  border-radius: 6px;
  background: ${props => props.theme.surface || '#fff'};
`;

const PartInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PartTime = styled.span`
  font-size: 12px;
  color: ${props => props.theme.textSecondary || '#666'};
`;

const PartText = styled.span`
  font-size: 14px;
  color: ${props => props.theme.text || '#000'};
  font-weight: 500;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const Button = styled.button`
  padding: 6px 12px;
  border: 1px solid ${props => props.theme.border || '#ccc'};
  border-radius: 4px;
  background: ${props => props.variant === 'danger' ? '#dc3545' : props.theme.primary || '#007bff'};
  color: white;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    opacity: 0.8;
  }
`;

export const PartsManager = ({ project, onUpdate }) => {
  const [parts, setParts] = useState(project.parts || []);

  useEffect(() => {
    setParts(project.parts || []);
  }, [project.parts]);

  /**
   * Add new part
   */
  const handleAddPart = (startTime = 0, endTime = 10) => {
    const newPart = {
      id: `part_${Date.now()}`,
      startTime,
      endTime,
      text: '',
      speaker: 'Speaker 1'
    };
    
    const updatedParts = [...parts, newPart];
    setParts(updatedParts);
    onUpdate(updatedParts);
  };

  /**
   * Delete part
   */
  const handleDeletePart = (partId) => {
    const updatedParts = parts.filter(p => p.id !== partId);
    setParts(updatedParts);
    onUpdate(updatedParts);
  };

  /**
   * Update part
   */
  const handleUpdatePart = (partId, updates) => {
    const updatedParts = parts.map(p => 
      p.id === partId ? { ...p, ...updates } : p
    );
    setParts(updatedParts);
    onUpdate(updatedParts);
  };

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '16px' 
      }}>
        <span style={{ 
          fontSize: '14px', 
          color: '#666',
          fontWeight: 500
        }}>
          {parts.length} {parts.length === 1 ? 'Part' : 'Parts'}
        </span>
        <Button 
          onClick={() => handleAddPart()}
          variant="primary"
          size="small"
        >
          <Plus size={14} />
          Add Part
        </Button>
      </div>
      
      <PartsList>
        {parts.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
            No parts yet. Click "Add Part" to create one.
          </div>
        ) : (
          parts.map(part => (
            <PartItem key={part.id}>
              <PartInfo>
                <PartTime>
                  {formatTime(part.startTime)} - {formatTime(part.endTime)}
                </PartTime>
                <PartText>
                  {part.text || part.name || 'Untitled Part'}
                </PartText>
              </PartInfo>
              <Actions>
                <Button onClick={() => handleUpdatePart(part.id, { text: 'Updated' })}>
                  <Edit3 size={14} />
                  Edit
                </Button>
                <Button variant="danger" onClick={() => handleDeletePart(part.id)}>
                  <Trash2 size={14} />
                  Delete
                </Button>
              </Actions>
            </PartItem>
          ))
        )}
      </PartsList>
    </div>
  );
};

